// istanbul ignore file

import styles from './DefaultFallback.module.scss';

const IS_DEV = process.env.NODE_ENV !== 'production';

export interface FallbackProps {
    name?: string;
    error?: Error;
    resetError?: () => void;
}
const DevFallback: React.FunctionComponent<FallbackProps> = ({ name, error, resetError }) => (
    <div className={styles.root}>
        <h3 className={styles.title}>Something went wrong</h3>
        <p className={styles.description}>
            This component encountered an error and could not be displayed. This is a dev-only
            warning to help debug the error. In production, this component will not be displayed
            unless a different <code>fallback</code> is provided to the <code>ErrorBoundary</code>{' '}
            component.
        </p>
        {error && (
            <div className={styles.errorDetails}>
                <summary className={styles.errorDetailsSummary}>Error details:</summary>
                <pre className={styles.errorStack}>
                    {error.message}
                    {error.stack && `\n\n${error.stack}`}
                </pre>
            </div>
        )}
        {resetError && (
            <div className={styles.buttonContainer}>
                <button onClick={resetError}>{'Try again'}</button>
            </div>
        )}
    </div>
);

/**
 * This is the default fallback component that is displayed when an error occurs.
 * In production mode, it renders an empty div.
 * In development, it renders a fallback UI with the error message and stack trace.
 */
export const DefaultFallback: React.FC<FallbackProps> = ({ error, resetError, name }) => {
    if (IS_DEV) {
        return <DevFallback name={name} error={error} resetError={resetError} />;
    }

    // Default fallback is an empty div in production
    return <div />;
};
